import React, { useState } from "react";

import DoneIcon from "@mui/icons-material/Done";
import SyncIcon from "@mui/icons-material/Sync";
import { IconButton, Menu, MenuItem, Stack, styled, Typography } from "@mui/material";
import { DealRoleGuard } from "components/guards";

import { RoleName } from "../../constants";

const StyledMenuItem = styled(MenuItem)(() => ({
  transition: "all 0.2s ease-in-out",
  padding: "12px 16px",
  borderRadius: "8px",
  margin: "4px 8px",
  ":hover": {
    backgroundColor: "#ECF1F8",
    transform: "translateY(-1px)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
  }
}));

const StyledIconButton = styled(IconButton)(() => ({
  height: "24px",
  width: "24px",
  transition: "all 0.2s ease-in-out",
  ":hover": {
    backgroundColor: "#F5F7FA",
    transform: "scale(1.1)"
  }
}));

interface Props {
  logo?: string;
  type?: string;
  username?: string;
  isLoginValid?: boolean;
  onReconnect?: () => void;
}

export const DataroomCompact: React.FC<Props> = ({ logo, type, username, isLoginValid, onReconnect }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleClose = () => {
    setAnchor(null);
  };

  const isConnectionValid = Boolean(isLoginValid);

  if (logo && type && username) {
    return (
      <Stack
        className="dataroom-compact-card"
        sx={{
          flexDirection: "row",
          alignItems: "center",
          gap: "12px",
          p: "12px",
          borderRadius: "8px",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          position: "relative",
          height: "56px",

          img: {
            flexShrink: 0,
            width: 48,
            height: 48,
            borderRadius: "12px"
          }
        }}
      >
        {/* Logo - Left side in white container */}
        <Stack
          sx={{
            width: "45px",
            height: "45px",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <img src={logo} alt="Dataroom logo" />
        </Stack>

        {/* Text Information - Middle section */}
        <Stack sx={{ flex: 1, alignItems: "flex-start" }}>
          <Typography
            sx={{
              color: "#374151",
              fontSize: "15px",
              fontWeight: 600,
              lineHeight: "20px",
              mb: "3px",
              letterSpacing: "-0.025em",
              textAlign: "left"
            }}
          >
            {type}
          </Typography>
          <Typography
            sx={{
              color: "#6B7280",
              fontSize: "13px",
              fontWeight: 400,
              lineHeight: "16px",
              textAlign: "left"
            }}
          >
            {username}
          </Typography>
        </Stack>

        {/* Separator */}
        <Stack
          sx={{
            width: "1px",
            height: "28px",
            backgroundColor: "#E5E7EB",
            mx: "6px"
          }}
        />

        {/* Status Indicator - Right side */}
        <DealRoleGuard availableRoles={[RoleName.Admin]}>
          {isConnectionValid ? (
            <Stack
              sx={{
                p: "6px",
                borderRadius: "50%",
                backgroundColor: "#F0FDF4",
                border: "1px solid #BBF7D0",
                flexShrink: 0
              }}
            >
              <DoneIcon sx={{ color: "#16A34A", fontSize: "16px" }} />
            </Stack>
          ) : (
            <>
              <StyledIconButton
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                  color: "#64748B",
                  backgroundColor: "#F8FAFC",
                  border: "1px solid #E2E8F0",
                  flexShrink: 0,
                  height: "32px",
                  width: "32px",
                  ":hover": {
                    backgroundColor: "#F1F5F9",
                    borderColor: "#CBD5E1"
                  }
                }}
              >
                <SyncIcon sx={{ color: "#64748B", fontSize: "20px" }} />
              </StyledIconButton>
              <Menu
                sx={{
                  ".MuiPaper-root": {
                    borderRadius: "16px",
                    mt: "12px",
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    border: "1px solid #F1F5F9",
                    overflow: "hidden"
                  }
                }}
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={handleClose}
              >
                <StyledMenuItem onClick={onReconnect}>Reconnect</StyledMenuItem>
              </Menu>
            </>
          )}
        </DealRoleGuard>
      </Stack>
    );
  }

  return null;
};
